/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ValidatorFn, Validators} from '@angular/forms';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputListModel} from '../input-list.model';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {ErrorCodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/error-code.model';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';

@Component({
    selector: 'app-error-code-modal',
    templateUrl: './error-code-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ErrorCodeModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<ErrorCodeModel>> {

    @ViewChild('errorName') nameInput: InputTextComponent;
    @ViewChild('errorCode') codeInput: InputTextComponent;

    readonly ENTITY_NAME = 'error code';
    readonly EDIT_TITLE = 'Edit error code';
    readonly ADD_TITLE = 'Add an error code';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\d{3}$/)],
    };

    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();

    constructor(public typeahead: TypeaheadHelperService, inputMessages: InputMessagesService) {
        super(inputMessages);
    }

    setEntity(element: EntityElementModel<ErrorCodeModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });
        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.nameInput.inputControl,
                this.nameMessages,
                this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });
        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        this.inputMessages.mandatoryWarnings(this.codeInput.inputControl, this.codeMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                '123'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(code, 'error', this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();

    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }
}
